import DOMPurify from 'dompurify';

// KML balloons contain untrusted HTML, so it is sanitized with DOMPurify before rendering.
const purifier = DOMPurify(window);
purifier.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
    }
    if (node.tagName === 'IMG') node.setAttribute('style', 'max-width: 100%');
});

export const sanitizeKmlHtml = html => purifier.sanitize(html, {
    FORBID_TAGS: ['style', 'form', 'input', 'button'],
    FORBID_ATTR: ['style'],
    ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
    RETURN_DOM_FRAGMENT: true,
});

const child = (node, name) => Array.from(node.children).find(element => element.localName === name);
const text = (node, name) => child(node, name)?.textContent;

export const getKmlClickDetails = (node) => {
    const extended = child(node, 'ExtendedData');
    const fields = [];

    if (extended) {
        Array.from(extended.getElementsByTagNameNS('*', 'Data'))
            .forEach((data) => {
                const name = data.getAttribute('name') || '';
                fields.push({
                    name,
                    label: text(data, 'displayName') || name,
                    value: text(data, 'value') || '',
                });
            });
    }

    return {
        name: text(node, 'name') || '',
        description: text(node, 'description') || '',
        address: text(node, 'address') || '',
        id: node.getAttribute('id') || '',
        fields,
    };
};

export const createKmlInfoContent = (details, balloon) => {
    const hasContent = details.name
        || details.description
        || balloon?.template;
    if (balloon?.hidden || !hasContent) return null;

    const container = document.createElement('div');
    Object.assign(container.style, {
        maxWidth: '420px',
        maxHeight: '320px',
        overflow: 'auto',
        ...(balloon?.background && { backgroundColor: balloon.background }),
        ...(balloon?.foreground && { color: balloon.foreground }),
    });

    if (balloon?.template !== undefined) {
        const values = new Map([
            ['name', details.name],
            ['address', details.address],
            ['id', details.id],
        ]);

        details.fields.forEach(({ name, value, label }) => {
            if (!values.has(name)) values.set(name, value);
            values.set(`${name}/displayName`, label);
        });

        const content = balloon.template.replace(/\$\[([^\]]+)\]/g, (_, key) => (
            key === 'description'
                ? details.description
                : String(values.get(key) ?? '')
        ));
        const template = document.createElement('div');
        template.appendChild(sanitizeKmlHtml(content));
        container.appendChild(template);
    } else {
        if (details.name) {
            const title = document.createElement('strong');
            title.textContent = details.name;
            container.appendChild(title);
        }
        if (details.description && details.description.trim() !== details.name.trim()) {
            const description = document.createElement('div');
            description.appendChild(sanitizeKmlHtml(details.description));
            container.appendChild(description);
        }
        if (details.fields.length) {
            const table = document.createElement('table');
            table.style.borderCollapse = 'collapse';

            details.fields.forEach(({ label, value }) => {
                const row = table.insertRow();
                [label, value].forEach((text1) => {
                    const cell = row.insertCell();
                    cell.textContent = text1;
                    cell.style.border = '1px solid #999';
                    cell.style.padding = '4px 8px';
                });
            });

            container.appendChild(table);
        }
    }

    return container;
};
